import * as z from "zod";
import { UseFormReturn } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import { Input } from "@/components/ui/input.tsx";
import { BadgeInput } from "@/components/ui/badgeInput";
import { H4 } from "@/components/ui/h4.tsx";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipTrigger,
  TooltipProvider,
  TooltipContent,
} from "@/components/ui/tooltip";
import { InfoIcon, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";

// Define the Zod schema
const arrayWithoutEmptyStrings = z
  .array(z.string())
  .min(1, "Interests is required")
  .transform((array) => array.filter((value) => value !== ""));

export const basicInfoFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  persona_id: z
    .string()
    .max(63, "Must have at most 63 characters")
    .optional()
    .transform((personaId) => personaId?.replace(/\s+/g, "")), // Remove spaces
  short_bio: z.string().min(1, "Short Bio is required"),
  persona: z.string().min(1, "AI Personality is required"),
  interests: arrayWithoutEmptyStrings,
});

export type BasicInfo = z.infer<typeof basicInfoFormSchema>;

type BasicInfoStepProps = {
  readOnly: boolean;
  form: UseFormReturn<BasicInfo>;
  onQuickCreateClick?: () => void;
};

export function BasicInfoStep({
  form,
  readOnly,
  onQuickCreateClick,
}: BasicInfoStepProps) {
  return (
    <Form {...form}>
      <form className="flex flex-col gap-y-1">
        <TooltipProvider delayDuration={0}>
          <div className="flex justify-between items-center mb-2">
            <H4 className="text-primary">Create Your AI Agent</H4>
            {onQuickCreateClick && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onQuickCreateClick}
                disabled={readOnly}
              >
                <Bot className="mr-2 h-4 w-4" />
                AI Quick Create
              </Button>
            )}
          </div>
          <p className="text-sm text-white mt-0 mb-5 align-top">
            We'll start with the core identity - name, occupation, and basic
            demographics. These fundamental details lay the groundwork for your
            AI's unique personality and set the stage for deeper customization.
          </p>
          {/* Use a div instead of nested form */}
          <div className="flex flex-col gap-y-4">
            <TooltipProvider delayDuration={0}>
              <H4 className="text-primary mb-4">Basic Info</H4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
                {/* Name Field */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex flex-row items-center w-max">
                            <FormLabel required>Name</FormLabel>
                            <InfoIcon className="pl-1 w-4" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-64">Name of the agent</p>
                        </TooltipContent>
                      </Tooltip>
                      <FormControl>
                        <Input autoFocus disabled={readOnly} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Interests Field */}
                <FormField
                  control={form.control}
                  name="interests"
                  render={({ field }) => (
                    <FormItem>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex flex-row items-center w-max">
                            <FormLabel required>
                              Interests (comma-separated)
                            </FormLabel>
                            <InfoIcon className="pl-1 w-4" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-64">
                            What the agent is interested in. e.g., Sports,
                            cryptocurrencies, Food
                          </p>
                        </TooltipContent>
                      </Tooltip>
                      <FormControl>
                        <BadgeInput
                          disabled={readOnly}
                          value={field.value}
                          onChange={(newTags) => field.onChange(newTags)}
                          placeholder="Add an interest"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Short Bio Field */}
                <FormField
                  control={form.control}
                  name="short_bio"
                  render={({ field }) => (
                    <FormItem>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex flex-row items-center w-max">
                            <FormLabel required>Short Bio</FormLabel>
                            <InfoIcon className="pl-1 w-4" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-64">
                            Short introduction to the agent background. The more
                            specific the description, the better the content
                            creation becomes.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                      <FormControl>
                        <Textarea rows={2} disabled={readOnly} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* AI Personality Field */}
                <FormField
                  control={form.control}
                  name="persona"
                  render={({ field }) => (
                    <FormItem>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex flex-row items-center w-max">
                            <FormLabel required>AI Personality</FormLabel>
                            <InfoIcon className="pl-1 w-4" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-64">
                            Describe how you want the agent to express itself.
                            Personality will influence general tone and content
                            details.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                      <FormControl>
                        <Textarea rows={2} disabled={readOnly} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </TooltipProvider>
          </div>
        </TooltipProvider>
      </form>
    </Form>
  );
}
